import { FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { resolveMailLanguage } from "../../emailLanguage";
import { withEntryQuery } from "../../entryParams";
import {
  APPLE_REPORT_PROBLEM_URL,
  GOOGLE_PLAY_REFUND_URL,
  CANCELLATION_EMAIL,
  PRIVACY_URL,
  WITHDRAWAL_POLICY_URL,
} from "../../externalLinks";
import { ApiFailure } from "../../restClient";
import { WITHDRAWAL_CONFIRM_PATH } from "../../routes";
import WithdrawalFailureNotice from "./WithdrawalFailureNotice";
import {
  PROBLEM_FIELDS,
  WithdrawalFormValues,
  previewWithdrawal,
} from "./withdrawalApi";
import { EMPTY_FORM, WithdrawalNavState, readNavState } from "./withdrawalFlow";
import "../public-page.css";

/** Field names that can carry a validation message. */
type FieldError = keyof WithdrawalFormValues;

// Deliberately loose: this only catches a typo before the request goes out.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Mirror the column lengths of the withdrawal record so an over-long entry is
// stopped in the field rather than coming back as a TooLong problem.
const MAX_NAME = 200;
const MAX_EMAIL = 320;
const MAX_CONTRACT_REFERENCE = 200;
const MAX_NOTE = 2000;

/**
 * Step one of the withdrawal function of § 356a BGB: the "Vertrag widerrufen"
 * button, permanently reachable and not behind a login, and the form behind it.
 *
 * The form asks for exactly what § 356a Abs. 1 Satz 2 lists (name, contract,
 * address for the receipt) plus an optional note. ⚠️ No reason and no further
 * required field: a withdrawal needs no reason, and every extra field would be a
 * hurdle in front of the right.
 *
 * Submitting the form only builds the confirmation page; the withdrawal itself
 * is given on WITHDRAWAL_CONFIRM_PATH.
 */
function WithdrawalPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const incoming = readNavState(location.state);

  const language = resolveMailLanguage(i18n.language || "de");

  const [formOpen, setFormOpen] = useState<boolean>(
    !!incoming.openForm || !!incoming.values,
  );
  const [values, setValues] = useState<WithdrawalFormValues>(
    incoming.values || EMPTY_FORM,
  );
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>(
    incoming.fieldErrors || [],
  );
  const [failure, setFailure] = useState<ApiFailure | null>(
    incoming.failure || null,
  );
  const [busy, setBusy] = useState(false);

  const nameInput = useRef<HTMLInputElement>(null);
  const revealedByButton = useRef(false);

  // Moves the focus into the form the button revealed, so keyboard and screen
  // reader users land where the next step is.
  useEffect(() => {
    if (formOpen && revealedByButton.current && nameInput.current) {
      nameInput.current.focus();
    }
  }, [formOpen]);

  /** The address a withdrawal can always be sent to instead. */
  const contactEmail =
    (failure && failure.body && failure.body.contactEmail) ||
    CANCELLATION_EMAIL;

  const setValue = <K extends keyof WithdrawalFormValues>(
    key: K,
    value: WithdrawalFormValues[K],
  ) => setValues((current) => ({ ...current, [key]: value }));

  const collectErrors = (): FieldError[] => {
    const errors: FieldError[] = [];

    if (values.name.trim().length === 0) {
      errors.push("name");
    }
    if (!EMAIL_PATTERN.test(values.email.trim())) {
      errors.push("email");
    }
    if (values.contractReference.trim().length === 0) {
      errors.push("contractReference");
    }

    return errors;
  };

  const hasError = (field: FieldError) => fieldErrors.indexOf(field) >= 0;

  const errorId = (field: FieldError) => `withdrawal-${field}-error`;

  /** The attributes that tie a field to its error message. */
  const errorProps = (field: FieldError) =>
    hasError(field)
      ? { "aria-invalid": true, "aria-describedby": errorId(field) }
      : {};

  const openForm = () => {
    revealedByButton.current = true;
    setFormOpen(true);
  };

  /** Form step: hand the declaration to the backend and go to the button. */
  const goToConfirm = async (event: FormEvent) => {
    event.preventDefault();

    const errors = collectErrors();
    setFieldErrors(errors);
    if (errors.length > 0) {
      return;
    }

    setBusy(true);
    setFailure(null);

    const result = await previewWithdrawal(values, language);

    setBusy(false);

    if (!result.ok) {
      setFailure(result);
      const problem = result.body && result.body.problem;
      const field = problem ? PROBLEM_FIELDS[problem] : undefined;
      setFieldErrors(field ? [field] : []);
      return;
    }

    const state: WithdrawalNavState = { values, preview: result.data };
    navigate(withEntryQuery(WITHDRAWAL_CONFIRM_PATH), { state });
  };

  const fieldError = (field: FieldError) =>
    hasError(field) ? (
      <span id={errorId(field)} className="pub-field-error">
        {t(`withdrawal.form.errors.${field}`)}
      </span>
    ) : null;

  return (
    <div className="pub-page">
      <button
        type="button"
        className="pub-back-link"
        onClick={() => navigate(withEntryQuery("/"))}
      >
        {t("app.backHome")}
      </button>

      <h1 className="pub-title">{t("withdrawal.start.title")}</h1>
      <p className="pub-lead">{t("withdrawal.start.lead")}</p>

      <div className="pub-notice pub-notice-info">
        <p>
          {t("withdrawal.start.storeNotice")}{" "}
          <a
            className="pub-link"
            href={APPLE_REPORT_PROBLEM_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("withdrawal.start.storeLink")}
          </a>
          {" · "}
          <a
            className="pub-link"
            href={GOOGLE_PLAY_REFUND_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("withdrawal.start.storeLinkGoogle")}
          </a>
        </p>
      </div>

      <section
        className="pub-panel"
        aria-label={t("withdrawal.start.rightTitle")}
      >
        <h2 className="pub-panel-title">{t("withdrawal.start.rightTitle")}</h2>
        <ul className="pub-list">
          <li>{t("withdrawal.start.right.period")}</li>
          <li>{t("withdrawal.start.right.inTime")}</li>
          <li>{t("withdrawal.start.right.details")}</li>
        </ul>
        <p className="pub-text">
          <a
            href={WITHDRAWAL_POLICY_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="pub-link"
          >
            {t("withdrawal.start.policyLink")}
          </a>
        </p>
      </section>

      {!formOpen ? (
        <section
          className="pub-panel"
          aria-label={t("withdrawal.start.stepsTitle")}
        >
          <h2 className="pub-panel-title">
            {t("withdrawal.start.stepsTitle")}
          </h2>
          <ol className="pub-steps">
            <li>{t("withdrawal.start.steps.details")}</li>
            <li>{t("withdrawal.start.steps.review")}</li>
            <li>{t("withdrawal.start.steps.confirm")}</li>
          </ol>

          {/*
            § 356a Abs. 1 BGB: a button labelled "Vertrag widerrufen" or with an
            equally unambiguous wording, highlighted and easy to find. Keep the
            label and do not pair it with a competing call to action.
          */}
          <button
            type="button"
            className="pub-primary-btn pub-primary-btn-block"
            onClick={openForm}
          >
            {t("withdrawal.start.button")}
          </button>
          <p className="pub-submit-hint">{t("withdrawal.start.buttonHint")}</p>
        </section>
      ) : null}

      {formOpen ? (
        <form id="withdrawal-form" onSubmit={goToConfirm} noValidate>
          <WithdrawalFailureNotice
            failure={failure}
            contactEmail={contactEmail}
          />

          <section
            className="pub-panel"
            aria-label={t("withdrawal.form.title")}
          >
            <h2 className="pub-panel-title">{t("withdrawal.form.title")}</h2>

            <div className="pub-field">
              <label className="pub-label" htmlFor="withdrawal-name">
                {t("withdrawal.form.fields.name")}
              </label>
              <input
                id="withdrawal-name"
                ref={nameInput}
                className={`pub-input${hasError("name") ? " pub-input-invalid" : ""}`}
                type="text"
                autoComplete="name"
                required
                maxLength={MAX_NAME}
                value={values.name}
                onChange={(event) => setValue("name", event.target.value)}
                {...errorProps("name")}
              />
              {fieldError("name")}
            </div>

            <div className="pub-field">
              <label className="pub-label" htmlFor="withdrawal-email">
                {t("withdrawal.form.fields.email")}
              </label>
              <input
                id="withdrawal-email"
                className={`pub-input${hasError("email") ? " pub-input-invalid" : ""}`}
                type="email"
                autoComplete="email"
                required
                maxLength={MAX_EMAIL}
                value={values.email}
                onChange={(event) => setValue("email", event.target.value)}
                {...errorProps("email")}
              />
              <span className="pub-hint">{t("withdrawal.form.hints.email")}</span>
              {fieldError("email")}
            </div>

            <div className="pub-field">
              <label className="pub-label" htmlFor="withdrawal-contract">
                {t("withdrawal.form.fields.contractReference")}
              </label>
              <input
                id="withdrawal-contract"
                className={`pub-input${
                  hasError("contractReference") ? " pub-input-invalid" : ""
                }`}
                type="text"
                required
                maxLength={MAX_CONTRACT_REFERENCE}
                placeholder={t("withdrawal.form.placeholders.contractReference")}
                value={values.contractReference}
                onChange={(event) =>
                  setValue("contractReference", event.target.value)
                }
                {...errorProps("contractReference")}
              />
              <span className="pub-hint">
                {t("withdrawal.form.hints.contractReference")}
              </span>
              {fieldError("contractReference")}
            </div>

            <div className="pub-field">
              <label className="pub-label" htmlFor="withdrawal-note">
                {t("withdrawal.form.fields.note")}{" "}
                <span className="pub-optional">
                  {t("withdrawal.form.optional")}
                </span>
              </label>
              <textarea
                id="withdrawal-note"
                className={`pub-textarea${
                  hasError("note") ? " pub-input-invalid" : ""
                }`}
                maxLength={MAX_NOTE}
                value={values.note}
                onChange={(event) => setValue("note", event.target.value)}
                {...errorProps("note")}
              />
              {fieldError("note")}
            </div>

            <button type="submit" className="pub-primary-btn" disabled={busy}>
              {busy
                ? t("withdrawal.form.checking")
                : t("withdrawal.form.continue")}
            </button>
            <p className="pub-submit-hint">
              {t("withdrawal.form.continueHint")}
            </p>
            {/*
              Art. 13 GDPR: the person filling this in may have no account here,
              so the information is owed at the moment he types his details.
            */}
            <p className="pub-submit-hint">
              {t("withdrawal.form.privacyHint")}{" "}
              <a
                href={PRIVACY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="pub-link"
              >
                {t("withdrawal.form.privacyLink")}
              </a>
            </p>
          </section>
        </form>
      ) : null}

      <section
        className="pub-panel"
        aria-label={t("withdrawal.start.otherWaysTitle")}
      >
        <h2 className="pub-panel-title">
          {t("withdrawal.start.otherWaysTitle")}
        </h2>
        <p className="pub-text">
          {t("withdrawal.start.otherWays.mail")}{" "}
          <a href={`mailto:${contactEmail}`} className="pub-link">
            {contactEmail}
          </a>
        </p>
      </section>
    </div>
  );
}

export default WithdrawalPage;
